import * as React from "react";

/**
 * The kind of overlay layer the nearest ancestor popup is, read by the layer
 * mounting inside it to decide whether to paint a backdrop.
 *
 * Every modal layer paints its own scrim. A dialog, alert, command palette or
 * sheet opened over another modal dims THAT layer, not just the page: the
 * parent stays legible behind the scrim and steps back (`--nested-dialogs`
 * drives its scale), so the child clearly owns focus while the user keeps
 * the context they will return to. Base UI renders a nested layer's backdrop
 * only when forced (`enabled: forceRender || !nested`), on the assumption
 * that the parent already dimmed the page — every backdrop here forces
 * itself on, so a layer opened from a NON-modal, backdrop-less panel (a
 * docked side panel is a Base UI dialog root too) gets its scrim as well.
 *
 * The one exception is a drawer stacked directly on a drawer. The parent
 * drawer carries the scrim there: its own styles answer `--nested-drawers`
 * with a scale step (the card stack) and it lays the page scrim's 32% wash
 * over its own face — an overlay, never a recolor of the fill — so a second
 * full-screen wash would double the dim on the page. A drawer therefore
 * keeps its backdrop off exactly when its nearest layer is another drawer;
 * every other primitive announces `"modal"` so a drawer opened from a dialog
 * still dims the dialog beneath it.
 */
export type OverlayLayerKind = "modal" | "drawer";

export const AncestorLayerContext: React.Context<OverlayLayerKind | null> =
  React.createContext<OverlayLayerKind | null>(null);

/**
 * The DOM footprint of every overlay layer: a positioned popup (`data-side`),
 * a dialog, a listbox, and any `*-popup` slot. One selector, so a host that
 * asks "did focus leave for an overlay I opened?" — an inline editor deciding
 * whether to deactivate — never restates which parts count as overlays.
 */
export const OVERLAY_SELECTOR =
  "[data-side], [role='dialog'], [role='listbox'], [data-slot$='-popup']";

/** Whether `element` sits inside an overlay layer. */
export function isInsideOverlay(element: Element | null | undefined): boolean {
  return element?.closest(OVERLAY_SELECTOR) != null;
}
